import { FailureStreak } from '../playback/failure-backoff.js';
import { monotonicNowMs } from '../sync/server-clock.js';
import { MemoryPlaybackEventStore } from '../adapters/memory-playback-event-store.js';
import { PlaybackEventBuffer } from '../playback/playback-event-buffer.js';
import { buildSurfaceIdentity } from '../identity/surface.js';
import { MemoryAssetStore } from '../adapters/memory-asset-store.js';
import { MemoryCredentialStore } from '../adapters/memory-credential-store.js';
import { WebCryptoSha256Hasher } from '../adapters/web-crypto-hasher.js';
import { createFetchDeviceApi } from '../api/device-api.js';
import { PlaylistCache } from '../cache/playlist-cache.js';
import { claimPairingCode } from '../pairing/claim.js';
import { awaitTvPairingSession, createTvPairingSession } from '../pairing/session.js';
import { consoleLogger } from '../ports/logger.js';
import { PlaylistPlayer } from '../playback/playlist-player.js';
import { createNetworkVideoUrlResolver } from '../playback/network-video-url.js';
import { WallPlayer } from '../playback/wall-player.js';
import { DeviceSyncLoop } from '../sync/sync-loop.js';
/**
 * Wires pairing → sync → cache → schedule → playback for HTML5 / Tizen shells.
 */
export function createPlayerRuntime(options) {
    const logger = options.logger ?? consoleLogger;
    const identity = buildSurfaceIdentity({
        platform: options.platform,
        appVersion: options.appVersion,
        ...(options.capabilities ? { capabilities: options.capabilities } : {}),
    });
    const credentials = options.credentials ?? new MemoryCredentialStore();
    const assets = options.assets ?? new MemoryAssetStore();
    const hasher = options.hasher ?? new WebCryptoSha256Hasher();
    const api = options.api ?? createFetchDeviceApi(options.apiBaseUrl);
    const cache = new PlaylistCache();
    const resolveNetworkUrl = createNetworkVideoUrlResolver({ api, credentials, logger });
    // Proof-of-Play: one queue for playlist and wall plays, sent after each sync.
    const playbackEvents = new PlaybackEventBuffer(options.playbackEventStore ?? new MemoryPlaybackEventStore(), { logger });
    let playbackEventsReady;
    const ensurePlaybackEventsReady = () => (playbackEventsReady ??= playbackEvents.init().catch((error) => {
        playbackEventsReady = undefined;
        throw error;
    }));
    // Proof-of-play must never stop playback: if the saved queue can't load, keep playing
    // with events in memory. The load is retried on the next sync.
    const tryPlaybackEventsReady = () => ensurePlaybackEventsReady().catch((error) => {
        logger.warn('Playback event queue not loaded, continuing without it', {
            error: error instanceof Error ? error.message : String(error),
        });
    });
    const player = new PlaylistPlayer(options.playback, assets, logger, resolveNetworkUrl, playbackEvents, {
        // Evaluated during playback, after syncLoop has been constructed.
        // Before the first clock sample, ServerClock uses local time.
        serverNow: () => syncLoop.serverClock.now(),
    });
    const wallPlayer = new WallPlayer(options.playback, assets, hasher, logger, { playbackEvents });
    let playbackTask = null;
    let wallTask = null;
    let scheduleTimer = null;
    /** While a wall plays, re-sample server time every minute (smoothed, never a jump). */
    const WALL_CLOCK_RESAMPLE_MS = 60_000;
    let clockResampleTimer = null;
    const startClockResampling = () => {
        if (clockResampleTimer)
            return;
        clockResampleTimer = setInterval(() => {
            void syncLoop.sampleServerTime(3, { snap: false });
        }, WALL_CLOCK_RESAMPLE_MS);
    };
    const stopClockResampling = () => {
        if (clockResampleTimer)
            clearInterval(clockResampleTimer);
        clockResampleTimer = null;
    };
    let lastWallKey = null;
    let lastReadyKey = null;
    let lastReadyEpoch = null;
    // Tile prepare (download + checksum + cache) runs in the background: the sync loop awaits
    // handleWallAssignment, so a 16 MB download must not hold up syncs and heartbeats.
    const prepareNowMs = options.wallPrepareNowMs ?? monotonicNowMs;
    let preparedKey = null;
    let preparingKey = null;
    let prepareFailedKey = null;
    const prepareFailures = new FailureStreak();
    let prepareRetryAtMs = 0;
    const resetWallPrepare = () => {
        preparedKey = null;
        preparingKey = null;
        prepareFailedKey = null;
        prepareFailures.succeed();
        prepareRetryAtMs = 0;
    };
    /**
     * Forget everything about the wall: prepare state and READY. Used when the wall assignment
     * goes away, and on unpair: unpairing wipes the asset cache and the server drops READY, so
     * a re-pair into the same wall must download again and send READY again.
     */
    const resetWallState = () => {
        resetWallPrepare();
        lastReadyKey = null;
        lastReadyEpoch = null;
    };
    /** Report READY once per timeline. Returns the epoch if the server set it in the reply. */
    const reportReadyOnce = async (token, assignment) => {
        const readyKey = `${assignment.publicationId}:${assignment.timelineId}`;
        if (lastReadyKey === readyKey)
            return lastReadyEpoch;
        try {
            const ready = await api.reportWallReady(token, {
                publicationId: assignment.publicationId,
                timelineId: assignment.timelineId,
            });
            lastReadyKey = readyKey;
            lastReadyEpoch = ready.epochStartAt ?? null;
            return lastReadyEpoch;
        }
        catch (error) {
            logger.warn('Wall READY report failed', {
                error: error instanceof Error ? error.message : String(error),
            });
            return null;
        }
    };
    /** Cache the tile in the background, then report READY. No READY while it runs or after it fails. */
    const startWallPrepare = (token, assignment, tileKey) => {
        if (preparingKey === tileKey)
            return;
        if (prepareFailedKey !== tileKey) {
            // A different tile: don't inherit the backoff from an older one.
            prepareFailures.succeed();
            prepareRetryAtMs = 0;
        }
        else if (prepareNowMs() < prepareRetryAtMs) {
            return;
        }
        preparingKey = tileKey;
        void wallPlayer
            .prepareAssignment(assignment, { downloadBytes: (url) => api.downloadBytes(url) })
            .then(async () => {
            if (preparingKey !== tileKey)
                return; // assignment changed while downloading
            preparingKey = null;
            preparedKey = tileKey;
            prepareFailedKey = null;
            prepareFailures.succeed();
            prepareRetryAtMs = 0;
            await reportReadyOnce(token, assignment);
        }, (error) => {
            if (preparingKey !== tileKey)
                return;
            preparingKey = null;
            prepareFailedKey = tileKey;
            if (prepareFailures.fail()) {
                logger.warn('Wall tile prepare failed', {
                    error: error instanceof Error ? error.message : String(error),
                });
            }
            // 5 s doubling to 60 s; a later sync retries after this.
            prepareRetryAtMs = prepareNowMs() + prepareFailures.backoffMs();
        });
    };
    let hydrated = false;
    const stopPlaylistPlayback = async () => {
        await player.stop();
        await playbackTask?.catch(() => undefined);
        playbackTask = null;
    };
    const stopWallPlayback = async () => {
        await wallPlayer.stop();
        await wallTask?.catch(() => undefined);
        stopClockResampling();
        wallTask = null;
        lastWallKey = null;
    };
    const stopAllPlayback = async () => {
        if (scheduleTimer)
            clearInterval(scheduleTimer);
        scheduleTimer = null;
        await stopWallPlayback();
        await stopPlaylistPlayback();
        // Blank only when leaving playback entirely (revoke / runtime stop).
        await options.playback.stop({ clear: true });
    };
    /** Resume CURRENT only when every manifest item is still hash-valid in the asset store. */
    const playCurrentIfReady = async () => {
        if (wallPlayer.getActiveKey())
            return false;
        const current = cache.getCurrent();
        if (!current)
            return false;
        for (const item of current.manifest.items) {
            const stored = await assets.get(item.assetId);
            if (!stored || stored.sha256 !== item.sha256)
                return false;
        }
        // Fully retire any prior loop before starting another — otherwise image
        // dwells and a shared stopped flag can leave zombie playlists racing.
        await stopPlaylistPlayback();
        playbackTask = player.playPlaylist(current).catch((error) => {
            logger.warn('Playback loop ended', {
                error: error instanceof Error ? error.message : String(error),
            });
        });
        return true;
    };
    const handleWallAssignment = async (assignment, meta) => {
        if (!assignment?.item) {
            await stopWallPlayback();
            resetWallState();
            return;
        }
        const credential = await credentials.get();
        if (!credential)
            return;
        const item = assignment.item;
        if (!item)
            return;
        const readyKey = `${assignment.publicationId}:${assignment.timelineId}`;
        const tileKey = `${readyKey}:${item.sha256}`;
        if (preparedKey !== tileKey) {
            // Not cached yet: no READY and no play until prepare succeeds.
            startWallPrepare(credential.token, assignment, tileKey);
            return;
        }
        // Normally already sent right after prepare; this retries it if that report failed.
        const epochFromReady = await reportReadyOnce(credential.token, assignment);
        if (epochFromReady && !assignment.epochStartAt) {
            assignment = { ...assignment, epochStartAt: epochFromReady };
        }
        if (!assignment.epochStartAt)
            return;
        const playKey = `${tileKey}:${assignment.epochStartAt}`;
        if (lastWallKey === playKey && wallTask)
            return;
        await stopPlaylistPlayback();
        await stopWallPlayback();
        lastWallKey = playKey;
        // Agree on server time before starting: a burst of fast samples, keeping the fastest.
        await syncLoop.sampleServerTime(5, { snap: true });
        startClockResampling();
        wallTask = wallPlayer
            .playAssignment(assignment, {
            downloadBytes: (url) => api.downloadBytes(url),
            serverTime: meta.serverTime,
            ...(meta.serverNow ? { serverNow: meta.serverNow } : {}),
        })
            .catch((error) => {
            logger.warn('Wall playback ended', {
                error: error instanceof Error ? error.message : String(error),
            });
            // Don't leave a settled task in place: that blocked every retry until a reload.
            if (lastWallKey === playKey) {
                wallTask = null;
                lastWallKey = null;
                preparedKey = null;
            }
        });
    };
    const syncLoop = new DeviceSyncLoop({
        api,
        credentials,
        cache,
        assets,
        hasher,
        identity,
        appVersion: options.appVersion,
        logger,
        ...(options.offlineState ? { offlineState: options.offlineState } : {}),
        playbackEvents,
        ...(options.syncIntervalMs !== undefined ? { syncIntervalMs: options.syncIntervalMs } : {}),
        ...(options.restartPlayer ? { restartPlayer: options.restartPlayer } : {}),
        onActivated: async () => {
            await playCurrentIfReady();
        },
        onScaleMode: (mode) => {
            options.playback.setScaleMode?.(mode);
        },
        onRotationDegrees: (degrees) => {
            options.playback.setRotationDegrees?.(degrees);
        },
        onWallAssignment: handleWallAssignment,
        ...(options.onIdleDisplay ? { onIdleDisplay: options.onIdleDisplay } : {}),
        onRevoked: async () => {
            await stopAllPlayback();
            resetWallState();
            await options.onRevoked?.();
        },
    });
    const hydrateFromOfflineState = async () => {
        if (hydrated || !options.offlineState)
            return;
        hydrated = true;
        const snapshot = await options.offlineState.loadPlaylistCache();
        if (snapshot) {
            cache.hydrate(snapshot);
            logger.info('Restored playlist cache from durable store', {
                current: snapshot.current
                    ? `${snapshot.current.ref.playlistId}@${snapshot.current.ref.version}`
                    : null,
            });
        }
        const schedule = await options.offlineState.loadSchedule();
        if (schedule) {
            syncLoop.restoreSchedule(schedule);
            logger.info('Restored schedule snapshot for offline dayparts');
        }
    };
    return {
        identity,
        async pair(code) {
            const response = await claimPairingCode({
                api,
                credentials,
                code,
                appVersion: options.appVersion,
                identity,
            });
            return { deviceId: response.deviceId, screenId: response.screenId };
        },
        async beginPairingSession() {
            return createTvPairingSession({
                api,
                credentials,
                appVersion: options.appVersion,
                identity,
            });
        },
        async awaitPairingSession(code, signal) {
            return awaitTvPairingSession({
                api,
                credentials,
                code,
                ...(signal ? { signal } : {}),
            });
        },
        async isPaired() {
            return (await credentials.get()) !== null;
        },
        async start() {
            await tryPlaybackEventsReady();
            await hydrateFromOfflineState();
            // Play last verified campaign immediately — do not wait for sync (ScreenTinker-class cold start).
            await playCurrentIfReady();
            syncLoop.start();
            scheduleTimer = setInterval(() => {
                if (wallPlayer.getActiveKey())
                    return;
                void syncLoop.evaluateScheduleLocally();
            }, options.scheduleTickMs ?? 30_000);
        },
        async stop() {
            syncLoop.stop();
            await stopAllPlayback();
        },
        syncOnce: async () => {
            await tryPlaybackEventsReady();
            await syncLoop.syncOnce();
        },
    };
}
//# sourceMappingURL=create-player-runtime.js.map